import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { SheetsDisplayGridRowDataFragment , SheetOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { createLinkCellFormatter, createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createObfuscatedLinkOrTextCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { SheetsValidSortFields } from '../sorting/sortProviders/SheetsGridSortProvider';

const shouldBeObfuscated: (rowData: SheetsDisplayGridRowData) => boolean = ({ name }) => name === null;

export interface SheetsDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly path: string | null;
  readonly workbookName: string | null;
  readonly workbookVizportalUrlId: string | null;
  readonly projectName: string | null;
  readonly projectVizportalUrlId: string | null;
  readonly ownerName: string | null;
  readonly ownerUsername: string | null;
  readonly ownerDomain: string | null;
}

export function getRowDataForSheet(sheet: SheetsDisplayGridRowDataFragment): SheetsDisplayGridRowData {
  return {
    id: sheet.id,
    name: sheet.name,
    path: sheet.path,
    workbookName: sheet.workbook ? sheet.workbook.name : null,
    workbookVizportalUrlId: sheet.workbook ? sheet.workbook.vizportalUrlId : null,
    projectName: sheet.workbook ? sheet.workbook.projectName : null,
    projectVizportalUrlId: sheet.workbook ? sheet.workbook.projectVizportalUrlId : null,
    ownerName: sheet.workbook ? sheet.workbook.owner.name : null,
    ownerUsername: sheet.workbook ? sheet.workbook.owner.username : null,
    ownerDomain: sheet.workbook ? sheet.workbook.owner.domain : null,
  };
}

type SheetsColumnFactory = ColumnFactory<
  GridSortProps<SheetsValidSortFields>,
  SheetsDisplayGridRowData,
  SheetsValidSortFields
>;

function columns(): ReadonlyArray<SheetsColumnFactory> {
  const cols: ReadonlyArray<SheetsColumnFactory> = [
    (sortParams) => ({
      name: 'name',
      dataKey: SheetOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkOrTextCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { path }) => path ? urls.viewPath(path) : null,
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbook',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderWorkbook(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkOrTextCellFormatter(
        ({ workbookName }) => workbookName === null,
        ({ workbookName }) => workbookName,
        (urls, { workbookVizportalUrlId }) => workbookVizportalUrlId ? urls.workbookLineagePath(workbookVizportalUrlId) : null
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'location',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderLocation(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ projectName }) => projectName || '',
        (urls, { projectVizportalUrlId }) => projectVizportalUrlId ? urls.projectPath(projectVizportalUrlId) : null
      ),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.Owner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkCellFormatter(
        // If ownerName, ownerUsername, and ownerDomain are null, there is likely a permission denied issue
        ({ ownerName, ownerUsername }) => ownerName || ownerUsername || '',
        (urls, { ownerUsername, ownerDomain }) => {
          if (ownerUsername && ownerDomain) {
            return urls.userPath(ownerUsername, ownerDomain);
          } else {
            return '';
          }
        }
      ),
      resizable: true,
      sortable: false,
    })
  ];

  return cols.filter(Boolean) as ReadonlyArray<SheetsColumnFactory>;
}

export type SheetsDisplayGridProps =
  UsualDisplayGridProps<SheetsDisplayGridRowData, GraphQLError> &
  GridSortProps<SheetsValidSortFields>;

export function SheetsDisplayGrid(props: SheetsDisplayGridProps): JSX.Element {
  const cols = columns().map(columnFactory => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
